import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { CsrfUtil } from './csrf-util';

export interface IAjaxUtil {
    get: (url: string, returnFullResponse?: boolean) => Promise<any>,
    post: (url: string, data?: any, returnFullResponse?: boolean) => Promise<any>,
    formSubmit: (url: string, data: AjaxFormDataType, additionalHeaders?: Record<string, string>, config?: AxiosRequestConfig) => Promise<string>,
    uploadFile: (url: string, data: FormData) => Promise<any>,
    formatUrl: (url: string) => string,
    postInsecure: (url: string, data?: any) => Promise<any>
    addClientBrowserId: (path: string) => string;
    getWithConfig: (url: string, config?: AxiosRequestConfig<any>) => Promise<AxiosResponse>;
}

/**
 * Vanity type to clarify the purpose of this object.
 */
export type AjaxFormDataType = {
    [Key: string]: string,
}

/**
 * Using Axios, make a HTTP Get request to a URL
 * @param url a string with full path to the API
 * @param returnFullResponse return the full response if true, otherwise, only return `data`
 * @return Promise of `data` or the full response from the API request
 * @throws Error | AxiosError you can use axios.isAxiosError(e) to deterin the error type.
 */
async function get(url: string, returnFullResponse?: boolean): Promise<AxiosResponse> {
    try {
        const res = await axios.get(url);
        if (res.status === 200) {
            return returnFullResponse ? res : res.data;
        }
        console.log('Received a non 200 status on GET', { response: res });
        // @todo - figure out what to do if we fail "status" === 200 cause basically it just dies.
    } catch (e) {
        console.log('unable to GET ', { error: e });
        throw e;
    }
}

/**
 * Using Axios, pass an object using HTTP Post and expect an object response.
 * axios converts the Object to a JSON string
 * If you need to pass Form data use the formSubmit method below.
 * @param url a string with full path to the API
 * @param data optional data object to be sent as the body of the post request
 * @param returnFullResponse return the full response if true, otherwise, only return `data`
 * @return Promise of `data` or the full response from the API request
 * @throws Error with the message from Axios error.
 */
async function post(url: string, data?: Object, returnFullResponse?: boolean): Promise<AxiosResponse> {
    const postData = data ?? {};
    const csrfToken = CsrfUtil.getCsrfToken();
    let config: AxiosRequestConfig = {};

    if (!!csrfToken) {
        config = {
            headers: {
                [CsrfUtil.CSRF_HEADER_TOKEN]: csrfToken
            }
        };
    }

    try {
        const res = await axios.post(url, postData, config);
        if (res.status === 200 || res.status === 201) {
            return returnFullResponse ? res : res.data;
        }
        console.log('Received a non 200/201 status on POST', { response: res });
        // @todo - figure out what to do if we fail "status" === 200/201 cause basically it just dies.
    } catch (e) {
        console.log('unable to POST ', { error: e });
        throw e;
    }
}

/**
 *
 * @param url
 * @param data AjaxFormDataType - a more specific name for [Key: string]: string
 * @param additionalHeaders {Record<string, string>} optional additional headers to be merged into the defaults headers on the request
 * @param config {AxiosRequestconfig} optional Axios config for request customization
 */
async function formSubmit(url: string, data: AjaxFormDataType): Promise<string> {
    const formData = new URLSearchParams();
    Object.keys(data).map((key) => formData.append(key, data[key]));

    let config: AxiosRequestConfig = {
        headers: {
            "Content-Type": "application/x-www-form-urlencoded"
        }
    };

    // Traditionally, we would add the CSRF token as a form-appended value, though the corresponding
    // attribute is generic to work with bost JSON-based requests and form requests, so we'll append
    // it as an additiona header on the request
    const csrfToken = CsrfUtil.getCsrfToken();
    if (!!csrfToken) {
        config = {
            headers: {
                ...config.headers,
                [CsrfUtil.CSRF_HEADER_TOKEN]: csrfToken,
            }
        }
    }

    try {
        const res = await axios({
            method: "post",
            url,
            data: formData,
            headers: config.headers
        });
        if (res.status === 200 && (res.statusText === "OK" || res.statusText === "Found")) {
            return res.data;
        }
        console.log('Received a non 200 status on x-www-form-urlencoded', { response: res });
        throw new Error('The response from formSubmit returned an unexpected response signature');
        // @todo - figure out what to do if we fail "status" === 200 cause basically it just dies.
    } catch (e) {
        console.log('unable to submit x-www-form-urlencoded data ', e);
        throw e;
    }
}

/**
 * Posts to an endpoint without CORS.
 * axios doesn't support mode:no-cors which is why the fetch method is being used here.
 * @param url
 * @param data
 */
async function postInsecure(url: string, data?: Object) {
    const res = await fetch(url, {
        method: 'POST',
        headers: {
            "Content-Type": "application/json"
        },
        body: JSON.stringify(data),
        mode: 'no-cors',
    });
    // If CORS was ignored, the body will be opaque. Just assume it worked.
    const body = res.ok ? await res.json() : undefined;
    return body;
}

async function uploadFile(url: string, formData: FormData) {
    const csrfToken = CsrfUtil.getCsrfToken();
    let config: AxiosRequestConfig = {};

    if (!!csrfToken) {
        config = {
            headers: {
                [CsrfUtil.CSRF_HEADER_TOKEN]: csrfToken
            }
        };
    }

    const res = await axios({
        method: "post",
        url,
        data: formData,
        headers: {
            ...config.headers,
            "Content-Type": "multipart/form-data",
            "Accept": "application/json",
        },
    });
    return res;
}

/**
 * @deprecated - This should not be used. Use addClientBrowserId(path) instead.
 * @param url - the path to the item 
 * @param settings - Depending on this argument, the path should be return unchanged. FormatURL shouldn't be called if there's no need to call it.
 * @returns string - the absolute URL path to the relative url that was passed in.
 */
function formatUrl(url: string, settings: any = null): string {
    console.warn('formatUrl(url, settings) is deprecated, please use addClientBrowserId instead');
    return addClientBrowserId(url);
}

/**
 * Generates a path prepended by application information
 * @param path - The path to the item in question.
 * @returns string - The absolute path to the relative that path that was passed in with application information included.
 */
const addClientBrowserId = (path: string): string => {
    const Dayforce = window['Dayforce'];
    if (!Dayforce) {
        // If for some reason, dayforce is undefined, assume standalone mode but warn the user.
        console.warn('Dayforce is undefined, the application is in standalone mode.');
        return path;
    }
    const { AppSettingsData } = Dayforce;
    if (!AppSettingsData) {
        throw new Error('AppSettingsData is undefined.');
    }
    const { applicationPath, clientBrowserId } = AppSettingsData;
    if (typeof clientBrowserId === 'undefined') {
        throw new Error('Client browser ID is undefined.');
    }
    const prepend = applicationPath + `${applicationPath.endsWith('/') ? '' : '/'}u/${clientBrowserId}`;
    // only modify the url if it hasn't already been formatted
    if (path?.substring(0, prepend.length) !== prepend) {
        return prepend + `/${path}`.replace(/\/+/g, '/');
    }
    // The url that was passed in was already modified.
    throw new Error('The URL was already formatted.');
}

/**
 * Using Axios, make a HTTP Get request to a URL with axios configuration object
 * @param url A url string
 * @param config An optional object with axios configuration for the request. 
 * If not provided, performs a normal get request.
 * @return Promise<AxiosResponse> response object from the API request
 * @throws Error | AxiosError you can use axios.isAxiosError(e) to deterin the error type.
 */
async function getWithConfig(url: string, config?: AxiosRequestConfig<any>): Promise<AxiosResponse> {
    try {
        const res = await axios.get(url, config);
        if (res.status === 200) {
            return res;
        }
        console.log("Received a non 200 status on GET", { response: res });
        // @todo - figure out what to do if we fail "status" === 200 cause basically it just dies.
    } catch (e) {
        console.log("unable to GET ", { error: e });
        throw e;
    }
}

export const AjaxUtil: IAjaxUtil = {
    get,
    post,
    formSubmit,
    uploadFile,
    formatUrl,
    addClientBrowserId,
    postInsecure,
    getWithConfig
};
